import { useState, useEffect, useContext, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Accordion, AccordionDetails, AccordionSummary, Box, Checkbox, FormControl, FormControlLabel, FormGroup, FormLabel, Radio, RadioGroup, Stack, TextField } from "@mui/material";
import { treeActions } from "../../redux/slices/treeSlice";
import { ParentScreenCtx } from "/src/pages/workbench_build/WorkbenchBuild";
import LabeledCheckbox from "../TemplateProperties/TPCheckbox";
import { cloneDeep, debounce, isArray } from "lodash";
import { eachDeep } from "deepdash-es/standalone";

export default function WorkflowProperties() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const parentScreen = useContext(ParentScreenCtx);
  const { leftTree, rightTree } = useSelector(state => state.treeData[parentScreen]);
  const translatedData = rightTree.tree.body.translatedData;
  const wfProperties = rightTree.tree.templateHeader;
  const [forceRerender, setForceRerender] = useState(false);
  const [prevWfKey, setPrevWfKey] = useState(leftTree.tree.body.selectedNode?.key);

  //property values
  const wfStateValue = wfProperties?.WORKFLOW?.WORKFLOW_STATE || "ENABLED"; //Workflow Enabled
  const wfTransDetailsValue = wfProperties?.WORKFLOW?.TRANSACTION_LOGGING_LEVEL || "0"; //Workflow Transaction Details
  const bamReportingValue = wfProperties?.WORKFLOW?.BAM_LOG_ENABLED === "TRUE" ? true : false; //Enable BAM Reporting
  const bypassIncompleteWfMValue = wfProperties?.WORKFLOW?.BYPASS_INCOMPLETE_WF_MANAGER === "TRUE" ? true : false; //Bypass Incomplete Workflow Manager
  const highVolMaxTrans = wfProperties?.WORKFLOW?.HIGH_VOL_MAX_TRANS ? wfProperties?.WORKFLOW?.HIGH_VOL_MAX_TRANS : "0"; //Optimize for Small High Volume Transactions - assume there will always be HIGH_VOL_MAX_TRANS. havent found a way to remove in in SWT workflow properties
  const optimizeMemoryValue = wfProperties?.WORKFLOW?.FORCE_LARGE_XLATE_OPTION === "TRUE" ? true : false; //Optimize Processing Memory Management
  const archiveDataValue = wfProperties?.WORKFLOW?.ARCHIVE_TRANSACTION_FILES ? wfProperties.WORKFLOW.ARCHIVE_TRANSACTION_FILES : "true"; //Workflow Data Archive

  //dispatch new workflow properties to state
  const setNewWorfklowProperties = (newKeyVals, rootVals) => {
    // example input: newKeyVals = {"WORKFLOW_STATE": "ENABLED"}

    //spread new key values to overwrite existing values
    dispatch(treeActions.setWorkflowProperties({
      ...wfProperties,
      ...rootVals,
      WORKFLOW: {
        ...wfProperties.WORKFLOW,
        ...newKeyVals
      }
    }));
  }

  //onChange handler for Workflow State radio buttons
  const handleWfStateChange = (event) => {
    let newVal = event?.target?.value;
    if (newVal) setNewWorfklowProperties({WORKFLOW_STATE: newVal});
  }

  //onChange handler for Workflow State radio buttons
  const handleWfTransDetailsChange = (event) => {
    let newVal = event?.target?.value;
    if (newVal) setNewWorfklowProperties({TRANSACTION_LOGGING_LEVEL: newVal});
  }

  const handleBamReportingChange = (event, checked) => {
    setNewWorfklowProperties({BAM_LOG_ENABLED: checked ? "TRUE" : "FALSE"});
  }

  // const handleProcessOnceChange = (event) => {
  //   let newVal = event?.target?.value;
  //   if (newVal === "true") {
  //     setNewWorfklowProperties({PROCESS_ONLY_ONCE: newVal, MAX_CONCURRENT_WF_THREADS: "1"});
  //   } else {
  //     setNewWorfklowProperties({PROCESS_ONLY_ONCE: "false"});
  //   }  
  // }

  // const handleMaxConcurrChange = (val) => {
  //   if (val && typeof val === "string") setNewWorfklowProperties({MAX_CONCURRENT_WF_THREADS: val});
  // }

  const handleBypassChange = (event, checked) => {
    setNewWorfklowProperties({BYPASS_INCOMPLETE_WF_MANAGER: checked ? "TRUE" : "FALSE"});
  }

  const handleHighVolMaxTransChange = (val) => {
    //remove leading zeros, just multiple the val by 1, and convert back to to string.
    setNewWorfklowProperties({HIGH_VOL_MAX_TRANS: (val * 1).toString()});
  }

  const handleOptimizeMemoryChange = (event, checked) => {
    setNewWorfklowProperties({FORCE_LARGE_XLATE_OPTION: checked ? "TRUE" : "FALSE"});
  }

  const handleDataArchiveChange = (event) => {
    let newVal = event?.target?.value;
    if (newVal) setNewWorfklowProperties({ARCHIVE_TRANSACTION_FILES: newVal});

    

    const modifyWorkflowData = (newArchiveDataVal) => {
      let newSelectedNode = cloneDeep(rightTree.tree.body.selectedNode);
    
      // Clone and deeply iterate through the tree data to update the relevant nodes.
      const updatedData = eachDeep(cloneDeep(translatedData), (obj) => {
        if (obj.ICON_DISPLAY === "OBJECTS" || obj.key === "wf-0") return; //skip root object.
        if (typeof obj !== "object" || obj.length) return;
        
        if (obj.ARCHIVE_TRANSACTION_FILES) {
          obj.ARCHIVE_TRANSACTION_FILES = newArchiveDataVal;
        }

        // get selectedNode if we loop through it during this process.
        if (obj?.key === newSelectedNode?.key) {
          newSelectedNode = obj;
        }
  
        if (isArray(obj?.children)) {
          return obj.children;
        }
      });
  
      // write new workflow tree data.
      dispatch(
        treeActions.setNewRightTreeTranslatedData({
          screen: parentScreen,
          type: rightTree.currentTab,
          data: updatedData,
        })
      );
      
      //write new selectedNode
      dispatch(
        treeActions.setRightTreeSelectedNode({
          screen: parentScreen,
          type: rightTree.currentTab,
          data: newSelectedNode,
        })
      );
    };

    if (newVal) modifyWorkflowData(newVal === "true" ? "TRUE" : "FALSE");
    return;
  }

  useEffect(() => {
    if (prevWfKey !== leftTree.tree.body.selectedNode?.key) {
      setPrevWfKey(leftTree.tree.body.selectedNode?.key);
      setForceRerender(true);
    }
  }, [leftTree.tree.body.selectedNode?.key]);

  useEffect(() => {
    if (forceRerender) setForceRerender(false);
  }, [forceRerender]);

  return (
    <FormGroup className="height-100 width-100 padding-0 overflow-auto no-flex-wrap">
      <Box className="" sx={{ width: 'calc(100% -  32px) !important', minWidth: 275, padding: '8px 16px' }}>
          {leftTree.tree.body.selectedNode?.type === "workflow" && !forceRerender ? (
            <Stack spacing={'16px'} className="tp-segment">
              
              {/* Workflow State radio buttons */}
              <WorkflowState wfStateValue={wfStateValue} handleWfStateChange={handleWfStateChange} />

              {/* Workflow Transaction Details radio buttons */}
              <WorkflowTransactionDetails wfTransDetailsValue={wfTransDetailsValue} handleWfTransDetailsChange={handleWfTransDetailsChange}/>

              {/* Enable BAM Reporting checkbox */}
              <EnableBamReporting bamReportingValue={bamReportingValue} handleBamReportingChange={handleBamReportingChange} />
            
              {/* Workflow Processing controls */}
              <WorkflowProcessing 
                // rootHasChildren={rootHasChildren}
                // processOnceValue={processOnceValue} 
                // maxConcurrentValue={maxConcurrentValue} 
                // handleProcessOnceChange={handleProcessOnceChange} 
                // handleMaxConcurrChange={handleMaxConcurrChange} 
              />
              
              {/* Bypass Incomplete Workflow Manager chekbox */}
              <BypassIncompleteWfM bypassIncompleteWfMValue={bypassIncompleteWfMValue} handleBypassChange={handleBypassChange} />
            
              {/* Optimize for Small High Volume Transactions checkbox */}
              <OptimizeMaxTrans highVolMaxTrans={highVolMaxTrans} handleHighVolMaxTransChange={handleHighVolMaxTransChange}/>

              {/* Optimize Processing Memory Management checkbox */}
              <OptimizeMemory optimizeMemoryValue={optimizeMemoryValue} handleOptimizeMemoryChange={handleOptimizeMemoryChange}/>
            
              {/* Workflow Data Archive */}
              <WorkflowDataArchive archiveDataValue={archiveDataValue} handleDataArchiveChange={handleDataArchiveChange}/>
            
            </Stack>
          ) : (
            <div className='panel-no-data'>{t('LBL_NO_WORKFLOW_ITEM_SELECTED')}</div>
          )}
      </Box>
    </FormGroup>
  )
}

//Workflow State radio buttons
function WorkflowState({wfStateValue, handleWfStateChange}) {
  const { t } = useTranslation();
  return (
    <FormControl>
      <FormLabel style={{color: "#666", fontWeight: "bold"}}>{t("WORKFLOW_STATE")}</FormLabel>
      <RadioGroup
        aria-labelledby="wf-state-label"
        value={wfStateValue}
        onChange={handleWfStateChange}
        name="wf-state"
        row
      >
        <FormControlLabel value="ENABLED" control={<Radio />} label={t("ENABLEDWF")} />
        <FormControlLabel value="DISABLED" control={<Radio />} label={t("DISABLEDWF")} />
      </RadioGroup>
    </FormControl>
  )
}

//Workflow Transaction Details radio buttons
function WorkflowTransactionDetails({wfTransDetailsValue, handleWfTransDetailsChange}) {
  const { t } = useTranslation();
  return (
    <FormControl>
      <FormLabel style={{color: "#666", fontWeight: "bold"}}>{t("LBL_WORKLOW_TRANSACTION_DETAILS")}</FormLabel>
      <RadioGroup
        aria-labelledby="wf-state-label"
        value={wfTransDetailsValue}
        onChange={handleWfTransDetailsChange}
        name="wf-trans-details"
        // row
      >
        <FormControlLabel value="0" control={<Radio />} label={t("LBL_LOG_ALL_WORKFLOW_DETAILS")} />
        <FormControlLabel value="4" control={<Radio />} label={t("LBL_LOG_WORKFLOW_DETAILS_ON_ERROR")} />
        <FormControlLabel value="5" control={<Radio />} label={t("LBL_LOG_WORKFLOW_STATS_ALL_ON_ERROR")} />
        <FormControlLabel value="2" control={<Radio />} label={t("LBL_LOG_NO_WORKFLOW_DETAILS")} />
        <FormControlLabel value="6" control={<Radio />} label={t("LOG_WORKFLOW_DETAILS_IF_DATA_FOUND_TRUE_OR_ON_ERROR")} />
      </RadioGroup>
    </FormControl>
  )
}

//Enable BAM Reporting checkbox
function EnableBamReporting({bamReportingValue, handleBamReportingChange}) {
  const { t } = useTranslation();
  return (
    <FormControlLabel
      label={t("LBL_LOG_BAM")}
      sx={{marginLeft: "-9px !important", marginTop: "8px !important"}}
      control={
        <Checkbox 
          name={t("LBL_LOG_BAM")}
          size={'small'}
          checked={bamReportingValue}
          onChange={handleBamReportingChange}
        />
      } 
    />
  )
}

//Workflow Processing controls
function WorkflowProcessing(props) {

  //TODO: on workflow change dispatch empty template
  //timeout 0
  // *conditional render wf properties based on if node is empty or not
  // dispatch new node
  // updated vals shou;ld reflect.

  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { 
    // processOnceValue, 
    // handleProcessOnceChange,
    // handleMaxConcurrChange,
    // rootHasChildren
  } = props;
  const parentScreen = useContext(ParentScreenCtx);
  const { leftTree, rightTree } = useSelector(state => state.treeData[parentScreen]);
  const rootHasChildren = rightTree?.tree?.body?.translatedData?.[0]?.children?.length > 0;
  const wfProperties = rightTree.tree.templateHeader;
  const maxConcurrentValue = wfProperties?.WORKFLOW?.MAX_CONCURRENT_WF_THREADS || "1"; //Max Concurrent Threads
  const processOnceValue = wfProperties?.WORKFLOW?.PROCESS_ONLY_ONCE === "true" ? "true" : "false"; //Process Only Once

  const [runRadioValue, setRunRadioValue] = useState(wfProperties?.WORKFLOW?.MAX_CONCURRENT_WF_THREADS === "1" ? "sequential" : "concurrent");
  const [maxThreadsInputValue, setMaxThreadsInputValue] = useState(maxConcurrentValue);

  //drill through workflow tree to find first GET_DATA node.
  const getFirstGetDataNode = () => {
    let targetNode = false;

    const drillNode = (node) => {
      if (node?.ACTION === "GET_DATA") {
        targetNode = node;
        return;
      }
      if (node?.children?.length > 0) {
        node.children.forEach(obj => {
          drillNode(obj);
        })
      }
    }

    if (rightTree?.tree?.body?.translatedData?.[0]) drillNode(rightTree?.tree?.body?.translatedData?.[0]);
    return targetNode;
  }

  const firstGetDataNode = getFirstGetDataNode();
  //"Workflows" doc says EMAIL and Command Prompt aswell, but they don't work when selected in Java client.
  const showProcessingComponent = (firstGetDataNode && ["FTP", "SSH", "BridgeGateFTP", "BridgeGateMSMQ", "BridgeGateMQSeries", "MSMQ", "MQSeries", "ActiveMQ", "AmazonSQS", "AS2", "Kafka", "FILE"].includes(firstGetDataNode.INBOUND?.ADAPTER_TYPE)) ? true : false;
  // const showProcessingComponent = true; //for dev testing only. forces Workflow Processing component to show. remove after component determined complete by QA.

  //dispatch new workflow properties to state
  const setNewWorfklowProperties = (newKeyVals, rootVals) => {
    // example input: newKeyVals = {"WORKFLOW_STATE": "ENABLED"}

    //spread new key values to overwrite existing values
    dispatch(treeActions.setWorkflowProperties({
      ...wfProperties,
      ...rootVals,
      WORKFLOW: {
        ...wfProperties.WORKFLOW,
        ...newKeyVals
      }
    }));
  }

  const handleProcessOnceChange = (event) => {
    let newVal = event?.target?.value;
    //TODO: java client shows "true" string for PROCESS_ONLY_ONCE value, so sticking with that for now. 
    //but this needs to be confirmed in the future before save functionality is to be considered complete.
    if (newVal === "true") {
      setNewWorfklowProperties({PROCESS_ONLY_ONCE: newVal, MAX_CONCURRENT_WF_THREADS: "1"});
    } else {
      setNewWorfklowProperties({PROCESS_ONLY_ONCE: "false"});
    }  
  }

  const handleSequentiallyClick = () => {
    setRunRadioValue("sequential");
    handleMaxConcurrChange("1");
    setMaxThreadsInputValue("1");
  }

  const handleMaxConcurrChange = (val) => {
    if (val && typeof val === "string") setNewWorfklowProperties({MAX_CONCURRENT_WF_THREADS: val});
  }

  const handleConcurrentlyClick = () => {
    setRunRadioValue("concurrent");
  }

  const handleMaxThreadsTextFieldChange = (event) => {
    const inputVal = event?.target?.value;
    if (typeof inputVal !== "string") return;
    if (inputVal === "" || (inputVal.match(/^[0-9]*$/) && parseInt(inputVal) < 1000)) {
      setMaxThreadsInputValue(inputVal);
    }
  }
  
  const handleMTBlur = (event) => {
    const inputVal = event?.target?.value;
    if (inputVal === "0" || inputVal === "") {
      handleMaxConcurrChange("1");
      setMaxThreadsInputValue("1");
    } else {
      handleMaxConcurrChange(inputVal);
      setMaxThreadsInputValue(inputVal);
    }
  }

  useEffect(() => {
    if (maxConcurrentValue !== maxThreadsInputValue) {
      setMaxThreadsInputValue(maxConcurrentValue);
    }
  }, [maxConcurrentValue]);

  return (
    <>
      {showProcessingComponent ? (
        <>
          <FormLabel style={{ color: "#666", fontWeight: "bold" }}>{t("LBL_WORKFLOW_PROCESSING")}</FormLabel>
          <FormControl sx={{
            pointerEvents: !rootHasChildren ? "none" : "unset",
            display: !rootHasChildren ? "hidden" : "unset",
            marginTop: "0px !important"
          }}>
            {/* <FormLabel style={{color: "#666", fontWeight: "bold"}}>{t("LBL_WORKFLOW_PROCESSING")}</FormLabel> */}

            <FormControlLabel
              style={{ width: "fit-content" }}
              label={t("LBL_PROCESS_ALL_DATA")}
              control={
                <Radio
                  value={"false"}
                  onClick={handleProcessOnceChange}
                  checked={processOnceValue === "false"}
                  disabled={!rootHasChildren}
                />
              }
            />
            {(rootHasChildren && processOnceValue === "false" && (
              <div className="flex-column" style={{ paddingLeft: "28px" }}>
                <FormControlLabel
                  value="sequential"
                  label={t("TXT_RUN_SEQUENTIALLY")}
                  disabled={!rootHasChildren || processOnceValue === "true"}
                  control={
                    <Radio
                      onClick={handleSequentiallyClick}
                      checked={runRadioValue === "sequential"}
                    />
                  }
                />
                <div className="flex">
                  <FormControlLabel
                    value="concurrent"
                    label={t("TXT_RUN_CONCURRENTLY")}
                    disabled={!rootHasChildren || processOnceValue === "true"}
                    control={
                      <Radio
                        onClick={handleConcurrentlyClick}
                        checked={runRadioValue === "concurrent"}
                      />
                    }
                  />
                  {(rootHasChildren && processOnceValue === "false" && runRadioValue === "concurrent") && (
                    <TextField
                      variant="outlined"
                      size="small"
                      autoComplete="off"
                      style={{ width: "60px" }}
                      inputProps={{ style: { height: "16px" } }}
                      value={processOnceValue === "true" ? "1" : maxThreadsInputValue}
                      disabled={!rootHasChildren || processOnceValue === "true" || runRadioValue === "sequential"}
                      onChange={handleMaxThreadsTextFieldChange}
                      // autoFocus
                      onBlur={handleMTBlur}
                    />
                  )}
                </div>
              </div>
            ))}

            <FormControlLabel
              style={{ width: "fit-content" }}
              label={t("LBL_PROCESS_ONLY_ONE_DATA")}
              control={
                <Radio
                  value={"true"}
                  onClick={handleProcessOnceChange}
                  checked={processOnceValue === "true"}
                  disabled={!rootHasChildren}
                />
              }
            />
          </FormControl>
        </>
      ) : ""}
    </>
  )
}

//Enable Bypass Incomplete Workflow Manager checkbox
function BypassIncompleteWfM({bypassIncompleteWfMValue, handleBypassChange}) {
  const { t } = useTranslation();
  return (
    <FormControlLabel
      label={t("LBL_BYPASS_INCOMPLETE_WF_MGR")}
      sx={{marginLeft: "-9px !important", marginTop: "8px !important"}}
      control={
        <Checkbox 
          name={t("LBL_BYPASS_INCOMPLETE_WF_MGR")}
          size={'small'}
          checked={bypassIncompleteWfMValue}
          onChange={handleBypassChange}
        />
      } 
    />
  )
}

//HIGH_VOL_MAX_TRANS

//Optimize for Small High Volume Transactions checkbox
function OptimizeMaxTrans({highVolMaxTrans, handleHighVolMaxTransChange}) {
  const { t } = useTranslation();
  const [checkboxVal, setCheckboxVal] = useState(highVolMaxTrans !== "0");
  
  //assume there will always be HIGH_VOL_MAX_TRANS
  //HIGH_VOL_MAX_TRANS

  const handleCheckboxChange = (event, checked) => {
    setCheckboxVal(checked);
    if (!checked) {
      handleHighVolMaxTransChange("0");
      // setTextInputValue("0");
    }
  }

  const handleTextFieldChange = (event) => {
    const inputVal = event?.target?.value;
    if (typeof inputVal !== "string") return;
    if (inputVal === "" || (inputVal.match(/^[0-9]*$/) && parseInt(inputVal) < 1000)) {
      // setTextInputValue(inputVal);
      handleHighVolMaxTransChange(inputVal);
    }
  }
  
  const handleFieldBlur = (event) => {
    const inputVal = event?.target?.value;
    if (inputVal === "") {
      handleHighVolMaxTransChange("0");
      // setTextInputValue("0");
    } else {
      handleHighVolMaxTransChange(inputVal);
      // setTextInputValue(inputVal);
    }
  }

  return (
    <div className="flex-column" style={{marginTop: "0px"}}>
      <FormControlLabel
        label={t("LBL_HIGH_VOLUME")}
        sx={{marginLeft: "-9px !important", marginTop: "8px !important"}}
        control={
          <Checkbox 
            name={t("LBL_HIGH_VOLUME")}
            size={'small'}
            checked={checkboxVal}
            onChange={handleCheckboxChange}
          />
        } 
      />
      {(checkboxVal || highVolMaxTrans > "0") && (
        <div style={{display: "flex", alignItems: "center"}}>
          <TextField
            variant="outlined"
            size="small"
            autoComplete="off"
            style={{ width: "60px", marginLeft: "28px"}}
            inputProps={{style: {height: "16px"}}}
            // label={t("LBL_HIGH_MAX")}
            value={highVolMaxTrans}
            onChange={handleTextFieldChange}
            // autoFocus
            onBlur={handleFieldBlur}
          />
          <span style={{fontSize: "12px", color: "#000", marginLeft: "8px"}}>{t("LBL_HIGH_MAX")}</span>
        </div>
      )}
      
      
    </div>
    
  )
}


//FORCE_LARGE_XLATE_OPTION
//LBL_LARGE_XLATE_OPTION
//Optimize Processing Memory Management checkbox
function OptimizeMemory({optimizeMemoryValue, handleOptimizeMemoryChange}) {
  const { t } = useTranslation();
  return (
    <FormControlLabel
      label={t("LBL_LARGE_XLATE_OPTION")}
      sx={{marginLeft: "-9px !important", marginTop: "8px !important"}}
      control={
        <Checkbox 
          name={t("LBL_LARGE_XLATE_OPTION")}
          size={'small'}
          checked={optimizeMemoryValue}
          onChange={handleOptimizeMemoryChange}
        />
      } 
    />
  )
}

//ARCHIVE_TRANSACTION_FILES
//WORKFLOW DATA ARCHIVE radio buttons
function WorkflowDataArchive({archiveDataValue, handleDataArchiveChange}) {
  //** cases for this component's control display states and interactions with child nodes in the WF template **
  //STATE: if all child nodes "TRUE", and ARCHIVE_TRANSACTION_FILES "true", show normal.
  //STATE: if all child nodes "FALSE", and ARCHIVE_TRANSACTION_FILES "false", show normal.
  //STATE: if not all child nodes "TRUE", and ARCHIVE_TRANSACTION_FILES true, show (overrides exist).
  //STATE: if not all child nodes "FALSE", and ARCHIVE_TRANSACTION_FILES false, show (overrides exist).
  //ACTION: if all child nodes "TRUE", and user changes ARCHIVE_TRANSACTION_FILES to "false", change all child nodes to "FALSE", show normal.
  //ACTION: if all child nodes "FALSE", and user changes ARCHIVE_TRANSACTION_FILES to "true", change all child nodes to "TRUE", show normal.
  //ACTION: if not all child nodes "TRUE", and user changes ARCHIVE_TRANSACTION_FILES to "true", change all child nodes to "TRUE", show normal.
  //ACTION: if not all child nodes "FALSE", and user changes ARCHIVE_TRANSACTION_FILES to "false", change all child nodes to "FALSE", show normal.
  const { t } = useTranslation();
  const parentScreen = useContext(ParentScreenCtx);
  const { rightTree } = useSelector(state => state.treeData[parentScreen]);

  
  //LABEL TEXT LOGIC
  //NONE = no children, or no children with ARCHIVE_TRANSACTION_FILES prop.
  //TRUE = all children have ARCHIVE_TRANSACTION_FILES prop set to "TRUE".
  //FALSE = all children have ARCHIVE_TRANSACTION_FILES prop set to "FALSE".
  //BOTH = children have a mix of "TRUE" and "FALSE" values for ARCHIVE_TRANSACTION_FILES prop.
  const scanTreeForAllTrueorFalse = () => {
    let boolFlag = "NONE";

    const drillNode = (node) => {
      if (node?.ARCHIVE_TRANSACTION_FILES) {
        if (node.ARCHIVE_TRANSACTION_FILES === "TRUE") {
          if (boolFlag === "NONE") boolFlag = "TRUE";
          if (boolFlag === "FALSE") boolFlag = "BOTH";
        } else if (node.ARCHIVE_TRANSACTION_FILES === "FALSE") {
          if (boolFlag === "NONE") boolFlag = "FALSE";
          if (boolFlag === "TRUE") boolFlag = "BOTH";
        }
      }
      if (node?.children?.length > 0) {
        node.children.forEach(obj => {
          drillNode(obj);
        })
      }
    }

    if (rightTree?.tree?.body?.translatedData?.[0]?.children?.length > 0) drillNode(rightTree?.tree?.body?.translatedData?.[0]);
    return boolFlag;
  }

  const allTrueFalseFlag = scanTreeForAllTrueorFalse();

  let trueLabel = t("LBL_ARCHIVE_ALL_DATA");
  let falseLabel = t("LBL_ARCHIVE_NO_DATA");

  //logic gates to determine control label for current data state.
  if (archiveDataValue === "true") {
    if (allTrueFalseFlag === "TRUE") {
      trueLabel = t("LBL_ARCHIVE_ALL_DATA");
    } else if (allTrueFalseFlag === "FALSE" || allTrueFalseFlag === "BOTH") {
      trueLabel = `${t("LBL_ARCHIVE_ALL_DATA")} ${t("LBL_OVERRIDES_EXIST")}`;
    }
  } else if (archiveDataValue === "false") {
    if (allTrueFalseFlag === "FALSE") {
      falseLabel = t("LBL_ARCHIVE_NO_DATA");
    } else if (allTrueFalseFlag === "TRUE" || allTrueFalseFlag === "BOTH") {
      falseLabel = `${t("LBL_ARCHIVE_NO_DATA")} ${t("LBL_OVERRIDES_EXIST")}`;
    }
  }

  return (
    <FormControl>
      <FormLabel style={{color: "#666", fontWeight: "bold"}}>{t("LBL_WORKFLOW_DATA_ARCHIVE")}</FormLabel>
      <RadioGroup
        aria-labelledby="wf-state-label"
        value={archiveDataValue}
        onChange={handleDataArchiveChange}
        name="wf-state"
      >
        <FormControlLabel value="true" control={<Radio />} label={trueLabel} />
        <FormControlLabel value="false" control={<Radio />} label={falseLabel} />
      </RadioGroup>
    </FormControl>
  )
}